import { toast } from 'react-toastify';

import APIService from 'services/api';
import Logger from 'utils/log';
import { getArrayWithUpdatedObject } from 'utils/array';
import { EAsyncStatus } from 'enums';
import { IPlace, PlaceDTO } from 'types';
import { AnyRestaruant } from 'types/restaurants';
import { LOGOUT } from './app';
import { IAppState } from './main';

export interface IRestaurantPlacesReducerState {
  places: IPlace[];
  status: EAsyncStatus;
  error: any;
  updatingPlaces: number[];
}

export const INITIAL_STATE: IRestaurantPlacesReducerState = {
  places: [],
  status: EAsyncStatus.IDLE,
  error: null,
  updatingPlaces: [],
};

export const SET_RESTAURANT_PLACES_LOADING = 'SET_RESTAURANT_PLACES_LOADING';
export const SET_RESTAURANT_PLACES_SUCCESS = 'SET_RESTAURANT_PLACES_SUCCESS';
export const SET_RESTAURANT_PLACES_ERROR = 'SET_RESTAURANT_PLACES_ERROR';

export const ADD_RESTAURANT_PLACE = 'ADD_RESTAURANT_PLACE';
export const CLEAR_RESTAURANT_PLACES = 'CLEAR_RESTAURANT_PLACES';

export const UPDATE_RESTAURANT_PLACE_REQUESTED =
  'UPDATE_RESTAURANT_PLACE_REQUESTED';
export const UPDATE_RESTAURANT_PLACE_SUCCEEDED =
  'UPDATE_RESTAURANT_PLACE_SUCCEEDED';
export const UPDATE_RESTAURANT_PLACE_FAILED = 'UPDATE_RESTAURANT_PLACE_FAILED';

function restaurantPlacesReducer(
  state = INITIAL_STATE,
  action: any
): IRestaurantPlacesReducerState {
  switch (action.type) {
    case SET_RESTAURANT_PLACES_LOADING:
      return {
        ...state,
        status: EAsyncStatus.PENDING,
        error: null,
      };
    case SET_RESTAURANT_PLACES_SUCCESS:
      return {
        ...state,
        places: action.payload,
        status: EAsyncStatus.RESOLVED,
        error: null,
      };
    case SET_RESTAURANT_PLACES_ERROR:
      return {
        ...state,
        places: [],
        status: EAsyncStatus.REJECTED,
        error: action.error,
      };
    case ADD_RESTAURANT_PLACE:
      return {
        ...state,
        places: [...state.places, action.payload],
      };
    case UPDATE_RESTAURANT_PLACE_REQUESTED:
      return {
        ...state,
        updatingPlaces: [...state.updatingPlaces, action.payload.id],
      };
    case UPDATE_RESTAURANT_PLACE_SUCCEEDED:
      return {
        ...state,
        places: getArrayWithUpdatedObject(state.places, action.payload),
        updatingPlaces: state.updatingPlaces.filter(
          (id) => id !== action.payload.id
        ),
      };
    case UPDATE_RESTAURANT_PLACE_FAILED:
      return {
        ...state,
        updatingPlaces: state.updatingPlaces.filter(
          (id) => id !== action.payload.id
        ),
      };
    case CLEAR_RESTAURANT_PLACES:
      return INITIAL_STATE;
    case LOGOUT:
      return INITIAL_STATE;
    default:
      return state;
  }
}

export const loadRestaurantPlaces =
  (restaurant: AnyRestaruant) => (dispatch) => {
    dispatch({ type: SET_RESTAURANT_PLACES_LOADING });
    const url = `/restaurants/${restaurant.id}/places`;
    return APIService.get(url)
      .then((places: IPlace[]) => {
        dispatch({
          type: SET_RESTAURANT_PLACES_SUCCESS,
          payload: places,
        });
        return places;
      })
      .catch((e) => {
        toast.error('Błąd pobierania sal');
        Logger.fetchError({ e, url, method: 'GET' });
        dispatch({
          type: SET_RESTAURANT_PLACES_ERROR,
          error: e,
        });
      });
  };

export const clearRestaurantPlaces = () => (dispatch) => {
  dispatch({ type: CLEAR_RESTAURANT_PLACES });
};

export const addPlace =
  (restaurant: AnyRestaruant, placeData: PlaceDTO) => (dispatch) => {
    const url = `/restaurants/${restaurant.id}/places`;
    return APIService.post(url, placeData)
      .then((newPlace: IPlace) => {
        toast.success(`Sala ${placeData.name} została dodana`);
        dispatch({
          type: ADD_RESTAURANT_PLACE,
          payload: newPlace,
        });
        return newPlace;
      })
      .catch(() => toast.error('Błąd dodawania sali'));
  };

export const updateRestaurantPlace =
  (restaurant: AnyRestaruant, payload: IPlace) => (dispatch) => {
    const url = `/restaurants/${restaurant.id}/places/${payload.id}`;
    dispatch({ type: UPDATE_RESTAURANT_PLACE_REQUESTED, payload: payload });
    return APIService.put(url, payload)
      .then((updatedPlace: IPlace) => {
        dispatch({
          type: UPDATE_RESTAURANT_PLACE_SUCCEEDED,
          payload: updatedPlace,
        });
        toast.success(`Sala ${updatedPlace.name} została zaktualizowana`);
        return updatedPlace;
      })
      .catch((e) => {
        dispatch({ type: UPDATE_RESTAURANT_PLACE_FAILED, payload });
        toast.error('Błąd aktualizacji sali');
        Logger.fetchError({
          e,
          url,
          method: 'PUT',
          payload: payload as unknown as Record<string, unknown>,
        });
      });
  };

export const selectPlaces = (state: IAppState): IPlace[] =>
  state.restaurantPlaces.places;

export const selectPlacesStatus = (state: IAppState): EAsyncStatus =>
  state.restaurantPlaces.status;

export const selectUpdatingPlaces = (state: IAppState): number[] =>
  state.restaurantPlaces.updatingPlaces;

export const selectPlacesIsLoading = (state: IAppState): boolean =>
  state.restaurantPlaces.status === EAsyncStatus.PENDING;

// Legacy selector name for backward compatibility - returns updating places array
export const selectPlacesLoading = (state: IAppState): number[] =>
  state.restaurantPlaces.updatingPlaces;

export default restaurantPlacesReducer;

